import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    common: {
      search: 'Rechercher',
      filter: 'Filtrer',
      export: 'Exporter',
      save: 'Enregistrer',
      cancel: 'Annuler',
      yes: 'Oui',
      no: 'Non',
      collapse: 'Réduire',
      expand: 'Développer',
      delete: 'Supprimer',
      edit: 'Modifier',
      view: 'Voir',
      add: 'Ajouter',
      update: 'Mettre à jour',
      confirm: 'Confirmer',
      close: 'Fermer',
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      from: 'De',
      to: 'À',
      all: 'Tous'
    },
    nav: {
      dashboard: 'Tableau de bord',
      financial: 'Financier',
      pilgrims: 'Pèlerins',
      groups: 'Groupes',
      settings: 'Paramètres',
      agencies: 'Agences',
      reports: 'Rapports'
    },
    dashboard: {
      title: 'Tableau de bord',
      subtitle: 'Vue d\'ensemble de votre agence',
      activePilgrimsMetric: 'Pèlerins actifs',
      pendingVisasMetric: 'Visas en attente',
      upcomingDepartures: 'Départs à venir',
      revenueThisMonth: 'Revenus ce mois',
      weekChange: 'cette semaine',
      daysAway: 'jours',
      viewProfile: 'Voir profil',
      contactPilgrim: 'Contacter pèlerin',
      downloadVisa: 'Télécharger visa',
      viewInvoice: 'Voir facture',
      pendingApproval: 'En attente d\'approbation',
      inProcessing: 'En cours de traitement',
      approvedThisMonth: 'Approuvés ce mois',
      needsReview: 'Nécessite révision',
      quickActions: 'Actions rapides',
      activeGroups: 'Groupes actifs',
      recentActivity: 'Activité récente',
      visaStatus: 'Statut des visas',
      newPilgrimRegistered: 'Nouveau pèlerin enregistré',
      missingDocumentAction: 'Document manquant - Action requise',
      visaApproved: 'Visa approuvé',
      paymentReceived: 'Paiement reçu',
      newPilgrimDesc: 'a été ajoutée au groupe Omra Mars 2026 avec tous les documents requis',
      missingDocDesc: 'Le passeport de Mohammed Hassan expire dans 45 jours. Renouvellement nécessaire avant le départ',
      visaApprovedDesc: 'a été approuvé par les autorités saoudiennes via Nusuk',
      paymentReceivedDesc: 'Acompte de 15,000 MAD reçu de Youssef Alami pour le groupe Hajj 2026'
    },
    groups: {
      title: 'Gestion des groupes',
      subtitle: 'Créez et gérez vos groupes de pèlerinage Omra et Hajj',
      createGroup: 'Créer un groupe',
      totalGroups: 'Total groupes',
      activeGroups: 'Groupes actifs',
      totalCapacity: 'Capacité totale',
      assignedPilgrims: 'Pèlerins assignés',
      availableSpots: 'Places disponibles',
      totalRevenue: 'Revenus totaux',
      searchPlaceholder: 'Rechercher par nom de groupe...',
      filterByType: 'Filtrer par type',
      filterByStatus: 'Filtrer par statut',
      allTypes: 'Tous les types',
      allStatuses: 'Tous les statuts',
      omra: 'Omra',
      hajj: 'Hajj',
      available: 'Places disponibles',
      filling: 'En cours de remplissage',
      full: 'Complet',
      allMonths: 'Tous les mois',
      january: 'Janvier',
      february: 'Février',
      march: 'Mars',
      april: 'Avril',
      may: 'Mai',
      june: 'Juin',
      july: 'Juillet',
      august: 'Août',
      september: 'Septembre',
      october: 'Octobre',
      november: 'Novembre',
      december: 'Décembre',
      allSync: 'Tous',
      synced: 'Synchronisé',
      pending: 'En attente',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce groupe ?',
      deleteError: 'Erreur lors de la suppression',
      itineraryManagement: 'Gestion de l\'itinéraire pour',
      departure: 'Départ',
      return: 'Retour',
      makkahHotel: 'Hôtel Makkah',
      madinahHotel: 'Hôtel Madinah',
      flight: 'Vol',
      notDefined: 'Non défini'
    },
    pilgrims: {
      title: 'Gestion des pèlerins',
      subtitle: 'Gérez tous vos pèlerins Omra et Hajj',
      addPilgrim: 'Ajouter pèlerin',
      totalPilgrims: 'Total pèlerins',
      activePilgrims: 'Pèlerins actifs',
      pendingDocuments: 'Documents en attente',
      searchPlaceholder: 'Rechercher par nom, passeport, téléphone...',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer ce pèlerin ?',
      deleteError: 'Erreur lors de la suppression',
      name: 'Nom',
      passport: 'Passeport',
      group: 'Groupe',
      visaStatus: 'Statut visa',
      actions: 'Actions',
      manageSubtitle: 'Gérez tous vos pèlerins et suivez leur progression',
      import: 'Importer',
      exportPDF: 'Export PDF',
      exportCSV: 'Export CSV',
      visaApprovalRate: 'Taux d\'approbation visa',
      completeDocuments: 'Documents complets',
      paymentsReceived: 'Paiements reçus',
      contact: 'Contact',
      visa: 'Visa',
      documents: 'Documents',
      payment: 'Paiement',
      progress: 'Progression',
      showing: 'Affichage',
      of: 'sur',
      pilgrimsText: 'pèlerins',
      previous: 'Précédent',
      next: 'Suivant'
    },
    visas: {
      title: 'Gestion des visas',
      subtitle: 'Suivez et gérez toutes les demandes de visa',
      updateError: 'Erreur lors de la mise à jour',
      pageTitle: 'Gestion des Visas',
      pageSubtitle: 'Suivez et gérez toutes les demandes de visa',
      // Filter Panel
      advancedFilters: 'Filtres avancés',
      resetFilters: 'Réinitialiser',
      allStatuses: 'Tous les statuts',
      allPriorities: 'Toutes les priorités',
      allDates: 'Toutes les dates',
      searchPilgrim: 'Rechercher pèlerin ou passeport...',
      total: 'Total',
      // Status
      status: 'Statut',
      pending: 'En attente',
      underReview: 'En révision',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      expired: 'Expiré',
      issued: 'Délivré',
      inReview: 'En révision',
      // Urgency
      urgency: 'Urgence',
      critical: 'Critique',
      urgent: 'Urgent',
      medium: 'Moyen',
      normal: 'Normal',
      // Expiration
      expiration: 'Expiration',
      expirationDate: 'Date d\'expiration',
      expiringSoon: 'Expire bientôt',
      valid: 'Valide',
      daysRemaining: 'jours restants',
      // Table
      pilgrimNameLabel: 'Nom du pèlerin',
      applicationDate: 'Date de demande',
      stage: 'Étape',
      processingStage: 'Étape de traitement',
      actions: 'Actions',
      view: 'Voir',
      noApplicationsFound: 'Aucune demande trouvée',
      // Workflow Panel
      workflowTitle: 'Flux de validation',
      approvalsPlural: 'approbations',
      approvalsSingular: 'approbation',
      pendingApproval: 'en attente',
      approve: 'Approuver',
      reject: 'Rejeter',
      requestDocs: 'Demander documents',
      requestDocsShort: 'Docs',
      submittedOn: 'Soumis le',
      noApprovalsWaiting: 'Aucune approbation en attente',
      // Critical Alerts
      criticalAlertsTitle: 'Alertes critiques',
      alertsPlural: 'alertes',
      alertsSingular: 'alerte',
      immediateAttention: 'nécessitent une attention immédiate',
      expirationNear: 'Expiration proche',
      missingDocuments: 'Documents manquants',
      processingDelay: 'Retard de traitement',
      complianceIssue: 'Problème de conformité',
      resolve: 'Résoudre',
      noCriticalAlerts: 'Aucune alerte critique',
      // Bulk Operations
      bulkOperationsTitle: 'Opérations groupées',
      requestsPlural: 'demandes',
      requestsSingular: 'demande',
      selected: 'sélectionnées',
      selectAction: 'Sélectionner une action',
      updateStatus: 'Mettre à jour le statut',
      requestDocuments: 'Demander des documents',
      sendNotification: 'Envoyer une notification',
      newStatus: 'Nouveau statut',
      notificationTemplate: 'Modèle de notification',
      visaApprovedTemplate: 'Visa approuvé',
      documentsRequiredTemplate: 'Documents requis',
      processingUpdateTemplate: 'Mise à jour du traitement',
      expirationReminderTemplate: 'Rappel d\'expiration',
      executeAction: 'Exécuter l\'action',
      sendEmail: 'Envoyer email',
      sendWhatsApp: 'Envoyer WhatsApp',
      selectRequestsPrompt: 'Sélectionnez des demandes pour effectuer des opérations groupées',
      // Compliance Tracking
      complianceTrackingTitle: 'Suivi de conformité',
      saudiCompliance: 'Conformité aux réglementations saoudiennes',
      nusukSync: 'Synchronisation Nusuk',
      nusukSyncDesc: 'Synchronisation automatique avec la plateforme Nusuk',
      regulatoryCompliance: 'Conformité réglementaire',
      saudiRegulations: 'Conformité aux réglementations saoudiennes',
      auditTrail: 'Piste d\'audit',
      auditTrailDesc: 'Enregistrement complet de toutes les modifications',
      autoArchiving: 'Archivage automatique',
      autoArchivingDesc: 'Archivage automatique des visas expirés',
      active: 'Actif',
      warning: 'Avertissement',
      inactive: 'Inactif',
      records: 'enregistrements',
      archived: 'archivés',
      // Notification Schedule
      scheduledNotifications: 'Notifications planifiées',
      notificationsPlural: 'notifications',
      notificationsSingular: 'notification',
      waiting: 'en attente',
      schedule: 'Planifier',
      email: 'Email',
      sms: 'SMS',
      whatsapp: 'WhatsApp',
      recipientsPlural: 'destinataires',
      recipientsSingular: 'destinataire',
      scheduledFor: 'Planifié pour',
      french: 'Français',
      arabic: 'Arabe',
      noScheduledNotifications: 'Aucune notification planifiée',
      // Visa Details Modal
      visaDetails: 'Détails du visa',
      passportNumber: 'Numéro de passeport',
      applicationDetails: 'Détails de la demande',
      visaStatus: 'Statut du visa',
      statusTimeline: 'Chronologie du statut',
      applicationSubmitted: 'Demande soumise',
      currentStage: 'Étape actuelle',
      visaIssued: 'Visa délivré',
      downloadVisa: 'Télécharger le visa',
      print: 'Imprimer'
    },
    documents: {
      title: 'Gestion des documents',
      subtitle: 'Générez et gérez tous vos documents',
      centerTitle: 'Centre de gestion documentaire',
      contracts: 'Contrats',
      contractsDesc: 'Contrats Omra & Hajj',
      officialLists: 'Listes officielles',
      officialListsDesc: 'Listes des pèlerins',
      convocations: 'Convocations',
      convocationsDesc: 'Documents de convocation',
      attestations: 'Attestations',
      attestationsDesc: 'Attestations Omra/Hajj',
      invoices: 'Factures',
      invoicesDesc: 'Factures et reçus',
      overview: 'Vue d\'ensemble',
      storage: 'Stockage',
      signatures: 'Signatures',
      templates: 'Modèles',
      newDocumentSigned: 'Nouveau document signé',
      newDocumentSignedDesc: 'Le contrat d\'Ahmed Benali a été signé avec succès',
      signaturePending: 'Signature en attente',
      signaturePendingDesc: '3 documents nécessitent votre signature',
      generateSuccess: 'Documents générés avec succès pour',
      secureStorage: 'Stockage sécurisé',
      searchDocuments: 'Rechercher des documents...',
      noDocumentsFound: 'Aucun document trouvé',
      preview: 'Aperçu',
      download: 'Télécharger',
      share: 'Partager',
      version: 'Version',
      signatureWorkflows: 'Flux de signatures',
      newWorkflow: 'Flux de travail nouveau',
      progress: 'Progression',
      createdOn: 'Créé le',
      deadline: 'Date limite',
      signed: 'Signé',
      generateDocuments: 'Générer des documents',
      documentTemplate: 'Modèle de document',
      chooseFormat: 'Choisir le format du document',
      pilgrimGroup: 'Groupe de pèlerins',
      selectGroup: 'Sélectionner le groupe concerné',
      documentLanguage: 'Langue du document',
      includeSignature: 'Inclure la signature électronique',
      addWorkflow: 'Ajouter un flux de signature',
      bulkGeneration: 'Génération en masse',
      generateForAll: 'Générer pour tous les pèlerins du groupe',
      importantInfo: 'Informations importantes',
      autoSaveInfo: 'Les documents créés seront automatiquement enregistrés dans le stockage sécurisé et envoyés par email aux destinataires concernés.',
      cancel: 'Annuler',
      generate: 'Générer',
      templateManagement: 'Gestion des modèles',
      newTemplate: 'Modèle nouveau',
      bilingual: 'Bilingue',
      usages: 'Utilisations',
      modify: 'Modifier',
      templateCustomization: 'Personnalisation du modèle',
      header: 'En-tête',
      footer: 'Pied de page',
      agencyLogo: 'Logo de l\'agence',
      legalInfo: 'Informations légales',
      newSignatureWorkflow: 'Flux de signature nouveau',
      configureSignature: 'Configurer le processus de signature',
      close: 'Fermer',
      workflowName: 'Nom du flux de travail',
      workflowNamePlaceholder: 'Ex: Contrats de groupe A - Janvier 2026',
      documentToSign: 'Document à signer',
      deadlineDate: 'Date limite',
      signers: 'Signataires',
      add: 'Ajouter',
      fullName: 'Nom complet',
      email: 'Email',
      remove: 'Supprimer',
      signatureProcess: 'Processus de signature',
      signatureProcessInfo: 'Les signataires recevront un email contenant un lien sécurisé pour signer le document électroniquement. Vous serez informé de chaque signature.',
      createWorkflow: 'Créer un flux de travail',
      shareDocument: 'Partager le document',
      recipientEmail: 'Email du destinataire',
      sendMeCopy: 'Envoyer une copie à moi',
      receiveCopy: 'Recevoir une copie du document par email',
      includeMessage: 'Inclure un message',
      addCustomMessage: 'Ajouter un message personnalisé',
      message: 'Message',
      enterMessage: 'Entrez votre message ici...',
      shareInfo: 'Le destinataire recevra un lien sécurisé pour télécharger le document. Le lien expire après 7 jours.',
      editTemplate: 'Modifier le modèle',
      previewTemplate: 'Aperçu du modèle',
      templateSettings: 'Paramètres du modèle',
      templateName: 'Nom du modèle',
      language: 'Langue',
      usage: 'Utilisation',
      documentsGenerated: 'Document généré',
      previewLabel: 'Aperçu',
      templatePreview: 'Ceci est un aperçu du modèle de document. Le contenu réel sera généré dynamiquement basé sur les données du pèlerin et du groupe.',
      availableVariables: 'Variables disponibles:',
      autoGenerated: 'Document auto-généré - Informations légales et détails de contact de l\'agence',
      save: 'Enregistrer',
      documentPreview: 'Aperçu du document',
      pilgrim: 'Pèlerin',
      documentDetails: 'Détails du document',
      fileSize: 'Taille du fichier',
      uploadDate: 'Date de téléchargement',
      status: 'Statut',
      pdfPreview: 'Aperçu PDF',
      contentWillDisplay: 'Le contenu du document s\'affichera ici',
      total: 'Total',
      standardTemplate: 'Modèle standard',
      detailedTemplate: 'Modèle détaillé',
      simplifiedTemplate: 'Modèle simplifié',
      french: 'Français',
      arabic: 'Arabe',
      both: 'Bilingue',
      administrator: 'Administrateur',
      agent: 'Agent',
      completed: 'Terminé',
      sent: 'Méssage'
    },
    communication: {
      title: 'Centre de communication',
      subtitle: 'Communiquez avec vos pèlerins',
      compose: 'Composer',
      timeline: 'Historique',
      templates: 'Modèles',
      scheduled: 'Planifiés',
      integrations: 'Intégrations',
      analytics: 'Analytiques',
      messageDelivered: 'Message livré',
      messageDeliveredDesc: 'Votre message à 45 pèlerins a été livré avec succès',
      deliveryFailed: 'Échec de livraison',
      deliveryFailedDesc: '3 messages n\'ont pas pu être livrés. Vérifiez les numéros de téléphone'
    },
    analytics: {
      title: 'Tableau de bord analytique',
      subtitle: 'Analysez les performances de votre agence',
      exportPDF: 'Exporter PDF',
      exportCSV: 'Exporter CSV',
      exporting: 'Export...',
      revenue: 'Revenus',
      pilgrims: 'Pèlerins',
      currentPeriod: 'Période actuelle',
      previousPeriod: 'Période précédente',
      threeMonths: '3 mois',
      sixMonths: '6 mois',
      oneYear: '1 an'
    },
    financial: {
      title: 'Gestion Financière',
      subtitle: 'Gestion des transactions et de la rentabilité',
      totalRevenue: 'Revenus Totaux',
      outstandingBalance: 'Solde en Attente',
      refundsProcessing: 'Remboursements en Cours',
      groupProfitability: 'Rentabilité par Groupe',
      vsLastMonth: 'vs mois dernier',
      vsLastQuarter: 'vs trimestre dernier',
      transactions: 'Transactions',
      revenue: 'Revenus',
      costs: 'Coûts',
      profit: 'Profit',
      margin: 'Marge',
      viewDetails: 'Voir détails',
      export: 'Exporter',
      currency: 'MAD',
      profitabilityAnalysis: 'Analyse de Rentabilité',
      costEntry: 'Entrée de Coûts',
      addCostEntry: 'Ajouter Coût',
      bulkCostImport: 'Import en Masse',
      roiAnalysisTitle: 'Analyse ROI et Rentabilité',
      roiAnalysisDescription: 'Visualisez les métriques de rentabilité, tendances saisonnières et analyse du seuil de rentabilité',
      exportPDF: 'Exporter PDF',
      exportCSV: 'Exporter CSV',
      hide: 'Masquer',
      showAnalysis: 'Afficher l\'analyse'
    },
    pilgrimProfile: {
      // Header & Actions
      caseNumber: 'Numéro de dossier',
      requestDocument: 'Demander document',
      processPayment: 'Traiter paiement',
      assignToGroup: 'Assigner au groupe',
      sendMessage: 'Envoyer message',
      
      // Status
      active: 'Actif',
      pending: 'En attente',
      completed: 'Terminé',
      cancelled: 'Annulé',
      
      // Tabs
      personalInfo: 'Informations personnelles',
      documents: 'Documents',
      travelInfo: 'Informations de voyage',
      financial: 'Financier',
      caseHistory: 'Historique du dossier',
      
      // Personal Info Tab
      edit: 'Modifier',
      save: 'Enregistrer',
      cancel: 'Annuler',
      identityDetails: 'Détails d\'identité',
      firstName: 'Prénom',
      lastName: 'Nom',
      arabicFirstName: 'Prénom en arabe',
      arabicLastName: 'Nom en arabe',
      dateOfBirth: 'Date de naissance',
      placeOfBirth: 'Lieu de naissance',
      gender: 'Genre',
      male: 'Homme',
      female: 'Femme',
      nationality: 'Nationalité',
      moroccan: 'Marocain',
      nationalId: 'Carte d\'identité nationale',
      
      // Passport Info
      passportInfo: 'Informations du passeport',
      passportNumber: 'Numéro de passeport',
      passportIssueDate: 'Date d\'émission',
      passportExpiryDate: 'Date d\'expiration',
      passportIssuePlace: 'Lieu d\'émission',
      
      // Contact Info
      contactInfo: 'Coordonnées',
      email: 'Email',
      phone: 'Téléphone',
      whatsapp: 'WhatsApp',
      address: 'Adresse',
      city: 'Ville',
      postalCode: 'Code postal',
      
      // Emergency Contact
      emergencyContact: 'Contact d\'urgence',
      emergencyContactName: 'Nom du contact',
      emergencyContactRelation: 'Relation',
      emergencyContactPhone: 'Téléphone',
      spouse: 'Conjoint',
      parent: 'Parent',
      sibling: 'Frère/Sœur',
      child: 'Enfant',
      other: 'Autre',
      
      // Documents Tab
      passportCopy: 'Copie du passeport',
      photo: 'Photo d\'identité',
      medicalCertificate: 'Certificat médical',
      visaDocument: 'Document de visa',
      notUploaded: 'Non téléchargé',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      approvedOn: 'Approuvé le',
      rejectedOn: 'Rejeté le',
      uploadedOn: 'Téléchargé le',
      view: 'Voir',
      approve: 'Approuver',
      reject: 'Rejeter',
      upload: 'Télécharger',
      
      // Travel Info Tab
      groupAssignment: 'Affectation au groupe',
      groupName: 'Nom du groupe',
      groupType: 'Type de groupe',
      departureDate: 'Date de départ',
      returnDate: 'Date de retour',
      accommodation: 'Hébergement',
      makkahHotel: 'Hôtel à La Mecque',
      madinahHotel: 'Hôtel à Médine',
      roomType: 'Type de chambre',
      flightInfo: 'Informations de vol',
      outboundFlight: 'Vol aller',
      returnFlight: 'Vol retour',
      flightNumber: 'Numéro de vol',
      airline: 'Compagnie aérienne',
      departure: 'Départ',
      arrival: 'Arrivée',
      nusukSync: 'Synchronisation Nusuk',
      status: 'Statut',
      synced: 'Synchronisé',
      lastSync: 'Dernière synchronisation',
      itinerary: 'Itinéraire',
      
      // Financial Tab
      totalAmount: 'Montant total',
      paidAmount: 'Montant payé',
      remainingBalance: 'Solde restant',
      paymentHistory: 'Historique des paiements',
      date: 'Date',
      amount: 'Montant',
      method: 'Méthode',
      reference: 'Référence',
      paid: 'Payé',
      refunded: 'Remboursé',
      cash: 'Espèces',
      card: 'Carte',
      transfer: 'Virement',
      cheque: 'Chèque',
      invoices: 'Factures',
      invoiceNumber: 'Numéro de facture',
      downloadInvoice: 'Télécharger facture',
      generateInvoice: 'Générer facture',
      
      // Case History Tab
      timeline: 'Chronologie',
      noHistory: 'Aucun historique disponible',
      fileCreated: 'Dossier créé',
      fileCreatedDesc: 'Nouveau dossier de pèlerin créé dans le système',
      documentsUploaded: 'Documents téléchargés',
      documentsUploadedDesc: 'Passeport et photo d\'identité téléchargés',
      documentsApproved: 'Documents approuvés',
      documentsApprovedDesc: 'Tous les documents ont été vérifiés et approuvés',
      paymentReceived: 'Paiement reçu',
      assignedToGroup: 'Assigné au groupe',
      documentsLabel: 'Documents',
      passportPhoto: 'Passeport, Photo',
      amountLabel: 'Montant',
      methodLabel: 'Méthode',
      groupLabel: 'Groupe',
      
      // ProfileHeader prompts and alerts
      enterGroupName: 'Entrez le nom du groupe pour assigner ce pèlerin:',
      assignmentSuccess: '{firstName} {lastName} a été assigné au groupe "{groupName}" avec succès!',
      invalidGroupName: 'Nom de groupe invalide!',
      
      // Mock data translations
      firstPaymentReceived: 'Premier acompte de 10,000 MAD reçu',
      assignedToGroupDesc: 'Affecté au Groupe Omra Mars 2026',
      marchOmraGroup: 'Groupe Omra Mars 2026',
      standardOmra: 'Omra Standard',
      darAlTawhidHotel: 'Hôtel Dar Al Tawhid',
      alAqeeqHotel: 'Hôtel Al Aqeeq',
      doubleRoom: 'Chambre double',
      itineraryDescription: '7 jours à La Mecque, 7 jours à Médine avec visites des lieux saints et accompagnement spirituel complet',
      
      // Notifications
      passportApprovedNotification: 'Le passeport de Fatima Hassan a été approuvé',
      paymentReceivedNotification: 'Paiement de 5000 MAD reçu pour le dossier #OM2026-1234',
      
      // Invoice and Payment handlers
      invoiceGenerationMessage: 'Génération de la facture pour {firstName} {lastName}...\nMontant total: {totalAmount} MAD\nMontant payé: {paidAmount} MAD\nSolde restant: {remainingBalance} MAD',
      enterPaymentAmount: 'Entrez le montant du paiement (Solde restant: {remainingBalance} MAD):',
      amountExceedsBalance: 'Le montant saisi dépasse le solde restant!',
      selectPaymentMethod: 'Méthode de paiement (cash/card/transfer/cheque):',
      paymentSuccessMessage: 'Paiement de {amount} MAD enregistré avec succès!\nMéthode: {method}\nNouveau solde: {newBalance} MAD',
      invalidAmount: 'Montant invalide!'
    },
    login: {
      title: 'Connexion',
      subtitle: 'Connectez-vous à votre espace de gestion',
      email: 'Adresse e-mail',
      emailPlaceholder: 'votre@email.com',
      emailRequired: 'L\'adresse e-mail est requise',
      password: 'Mot de passe',
      passwordPlaceholder: 'Votre mot de passe',
      passwordRequired: 'Le mot de passe est requis',
      rememberMe: 'Se souvenir de moi',
      forgotPassword: 'Mot de passe oublié ?',
      signIn: 'Se connecter',
      invalidCredentials: 'Email ou mot de passe incorrect',
      noAccount: 'Pas encore de compte ?',
      register: 'S\'inscrire',
      networkError: 'Erreur de connexion. Vérifiez votre connexion internet.',
      loginSuccess: 'Connexion réussie'
    },
    error: {
      title: 'Quelque chose s\'est mal passé',
      message: 'Nous avons rencontré une erreur inattendue lors du traitement de votre demande.',
      back: 'Retour'
    },
    notFound: {
      title: 'Page non trouvée',
      message: 'La page que vous recherchez n\'existe pas. Retournons en arrière !',
      goBack: 'Retour',
      backToHome: 'Retour à l\'accueil'
    }
  },
  ar: {
    common: {
      search: 'بحث',
      filter: 'تصفية',
      export: 'تصدير',
      save: 'حفظ',
      cancel: 'إلغاء',
      yes: 'نعم',
      no: 'لا',
      collapse: 'طي',
      expand: 'توسيع',
      delete: 'حذف',
      edit: 'تعديل',
      view: 'عرض',
      add: 'إضافة',
      update: 'تحديث',
      confirm: 'تأكيد',
      close: 'إغلاق',
      loading: 'جاري التحميل...',
      error: 'خطأ',
      success: 'نجح',
      from: 'من',
      to: 'إلى',
      all: 'الكل'
    },
    nav: {
      dashboard: 'لوحة التحكم',
      financial: 'المالية',
      pilgrims: 'الحجاج',
      groups: 'المجموعات',
      settings: 'الإعدادات',
      agencies: 'الوكالات',
      reports: 'التقارير'
    },
    dashboard: {
      title: 'لوحة التحكم',
      subtitle: 'نظرة عامة على وكالتك',
      agencyDashboard: 'لوحة تحكم الوكالة',
      dashboardOverview: 'نظرة عامة على لوحة التحكم',
      activePilgrimsMetric: 'الحجاج النشطون',
      pendingVisasMetric: 'التأشيرات المعلقة',
      upcomingDepartures: 'المغادرات القادمة',
      revenueThisMonth: 'الإيرادات هذا الشهر',
      weekChange: 'هذا الأسبوع',
      daysAway: 'يوماً',
      viewProfile: 'عرض الملف الشخصي',
      contactPilgrim: 'الاتصال بالحاج',
      downloadVisa: 'تحميل التأشيرة',
      viewInvoice: 'عرض الفاتورة',
      pendingApproval: 'في انتظار الموافقة',
      inProcessing: 'قيد المعالجة',
      approvedThisMonth: 'تمت الموافقة هذا الشهر',
      needsReview: 'يحتاج إلى مراجعة',
      quickActions: 'إجراءات سريعة',
      activeGroups: 'المجموعات النشطة',
      recentActivity: 'النشاط الأخير',
      recentActivities: 'الأنشطة الأخيرة',
      visaStatus: 'حالة التأشيرات',
      viewAll: 'عرض الكل',
      manageGroups: 'إدارة المجموعات',
      financialSummary: 'الملخص المالي',
      totalRevenue: 'إجمالي الإيرادات',
      pendingPayments: 'المدفوعات المعلقة',
      netProfit: 'صافي الربح',
      criticalAlerts: 'التنبيهات الحرجة',
      viewPilgrims: 'عرض الحجاج',
      sendReminder: 'إرسال تذكير',
      manageGroup: 'إدارة المجموعة',
      newPilgrim: 'حاج جديد',
      createGroup: 'إنشاء مجموعة',
      processVisa: 'معالجة التأشيرة',
      finances: 'المالية',
      newPilgrimRegistered: 'تسجيل حاج جديد',
      missingDocumentAction: 'مستند مفقود - إجراء مطلوب',
      visaApproved: 'تأشيرة موافق عليها',
      paymentReceived: 'دفعة مستلمة',
      newPilgrimDesc: 'تمت إضافة فاطمة الزهراء بناني إلى مجموعة عمرة مارس 2026 مع جميع المستندات المطلوبة',
      missingDocDesc: 'جواز سفر محمد حسان ينتهي في 45 يوماً. التجديد ضروري قبل المغادرة',
      visaApprovedDesc: 'تمت الموافقة على تأشيرة عمرة أمينة الإدريسي من قبل السلطات السعودية عبر نسك',
      paymentReceivedDesc: 'تم استلام دفعة مقدمة 15,000 درهم من يوسف العلمي لمجموعة حج 2026'
    },
    groups: {
      title: 'إدارة المجموعات',
      subtitle: 'أنشئ وأدر مجموعات الحج والعمرة',
      createGroup: 'إنشاء مجموعة',
      totalGroups: 'إجمالي المجموعات',
      activeGroups: 'المجموعات النشطة',
      totalCapacity: 'السعة الإجمالية',
      assignedPilgrims: 'الحجاج المعينون',
      availableSpots: 'الأماكن المتاحة',
      totalRevenue: 'إجمالي الإيرادات',
      searchPlaceholder: 'البحث باسم المجموعة...',
      filterByType: 'تصفية حسب النوع',
      filterByStatus: 'تصفية حسب الحالة',
      allTypes: 'جميع الأنواع',
      allStatuses: 'جميع الحالات',
      omra: 'عمرة',
      hajj: 'حج',
      available: 'أماكن متاحة',
      filling: 'قيد الملء',
      full: 'ممتلئ',
      allMonths: 'جميع الأشهر',
      january: 'يناير',
      february: 'فبراير',
      march: 'مارس',
      april: 'أبريل',
      may: 'مايو',
      june: 'يونيو',
      july: 'يوليو',
      august: 'أغسطس',
      september: 'سبتمبر',
      october: 'أكتوبر',
      november: 'نوفمبر',
      december: 'ديسمبر',
      allSync: 'الكل',
      synced: 'متزامن',
      pending: 'في الانتظار',
      confirmDelete: 'هل أنت متأكد من حذف هذه المجموعة؟',
      deleteError: 'خطأ في الحذف',
      itineraryManagement: 'إدارة المسار لـ',
      departure: 'المغادرة',
      return: 'العودة',
      makkahHotel: 'فندق مكة',
      madinahHotel: 'فندق المدينة',
      flight: 'رقم الرحلة',
      notDefined: 'غير محدد'
    },
    pilgrims: {
      title: 'إدارة الحجاج',
      subtitle: 'إدارة جميع حجاج العمرة والحج',
      addPilgrim: 'إضافة حاج',
      totalPilgrims: 'إجمالي الحجاج',
      activePilgrims: 'الحجاج النشطون',
      pendingDocuments: 'المستندات المعلقة',
      searchPlaceholder: 'البحث بالاسم، جواز السفر، الهاتف...',
      confirmDelete: 'هل أنت متأكد من حذف هذا الحاج؟',
      deleteError: 'خطأ في الحذف',
      name: 'الاسم',
      passport: 'جواز السفر',
      group: 'المجموعة',
      visaStatus: 'حالة التأشيرة',
      actions: 'الإجراءات',
      manageSubtitle: 'إدارة جميع الحجاج وتتبع تقدمهم',
      import: 'استيراد',
      exportPDF: 'تصدير PDF',
      exportCSV: 'تصدير CSV',
      visaApprovalRate: 'معدل الموافقة على التأشيرة',
      completeDocuments: 'الوثائق المكتملة',
      paymentsReceived: 'المدفوعات المستلمة',
      contact: 'الاتصال',
      visa: 'التأشيرة',
      documents: 'الوثائق',
      payment: 'الدفع',
      progress: 'التقدم',
      showing: 'عرض',
      of: 'من',
      pilgrimsText: 'حجاج',
      previous: 'السابق',
      next: 'التالي'
    },
    visas: {
      title: 'إدارة التأشيرات',
      subtitle: 'تتبع وإدارة جميع طلبات التأشيرات',
      updateError: 'خطأ في التحديث',
      pageTitle: 'إدارة التأشيرات',
      pageSubtitle: 'تتبع وإدارة جميع طلبات التأشيرات',
      // Filter Panel
      advancedFilters: 'فلاتر متقدمة',
      resetFilters: 'إعادة تعيين',
      allStatuses: 'جميع الحالات',
      allPriorities: 'جميع الأولويات',
      allDates: 'جميع التواريخ',
      searchPilgrim: 'البحث عن حاج أو جواز سفر...',
      total: 'الإجمالي',
      // Status
      status: 'الحالة',
      pending: 'قيد الانتظار',
      underReview: 'قيد المراجعة',
      approved: 'موافق عليه',
      rejected: 'مرفوض',
      expired: 'منتهي الصلاحية',
      issued: 'صادر',
      inReview: 'قيد المراجعة',
      // Urgency
      urgency: 'الأولوية',
      critical: 'حرج',
      urgent: 'عاجل',
      medium: 'متوسط',
      normal: 'عادي',
      // Expiration
      expiration: 'انتهاء الصلاحية',
      expirationDate: 'تاريخ انتهاء الصلاحية',
      expiringSoon: 'تنتهي قريباً',
      valid: 'صالح',
      daysRemaining: 'يوم متبقي',
      // Table
      pilgrimNameLabel: 'اسم الحاج',
      applicationDate: 'تاريخ الطلب',
      stage: 'المرحلة',
      processingStage: 'مرحلة المعالجة',
      actions: 'الإجراءات',
      view: 'عرض',
      noApplicationsFound: 'لم يتم العثور على طلبات',
      // Workflow Panel
      workflowTitle: 'سير عمل التحقق',
      approvalsPlural: 'موافقات',
      approvalsSingular: 'موافقة',
      pendingApproval: 'في انتظار الموافقة',
      approve: 'الموافقة',
      reject: 'رفض',
      requestDocs: 'طلب مستندات',
      requestDocsShort: 'مستندات',
      submittedOn: 'تم التقديم في',
      noApprovalsWaiting: 'لا توجد موافقات في الانتظار',
      // Critical Alerts
      criticalAlertsTitle: 'تنبيهات حرجة',
      alertsPlural: 'تنبيهات',
      alertsSingular: 'تنبيه',
      immediateAttention: 'تتطلب اهتماماً فورياً',
      expirationNear: 'اقتراب انتهاء الصلاحية',
      missingDocuments: 'مستندات مفقودة',
      processingDelay: 'تأخير في المعالجة',
      complianceIssue: 'مشكلة امتثال',
      resolve: 'حل',
      noCriticalAlerts: 'لا توجد تنبيهات حرجة',
      // Bulk Operations
      bulkOperationsTitle: 'العمليات الجماعية',
      requestsPlural: 'طلبات',
      requestsSingular: 'طلب',
      selected: 'محددة',
      selectAction: 'اختر إجراء',
      updateStatus: 'تحديث الحالة',
      requestDocuments: 'طلب مستندات',
      sendNotification: 'إرسال إشعار',
      newStatus: 'الحالة الجديدة',
      notificationTemplate: 'قالب الإشعار',
      visaApprovedTemplate: 'تأشيرة موافق عليها',
      documentsRequiredTemplate: 'مستندات مطلوبة',
      processingUpdateTemplate: 'تحديث المعالجة',
      expirationReminderTemplate: 'تذكير بانتهاء الصلاحية',
      executeAction: 'تنفيذ الإجراء',
      sendEmail: 'إرسال بريد إلكتروني',
      sendWhatsApp: 'إرسال واتساب',
      selectRequestsPrompt: 'حدد طلبات لتنفيذ عمليات جماعية',
      // Compliance Tracking
      complianceTrackingTitle: 'تتبع الامتثال',
      saudiCompliance: 'الامتثال للوائح السعودية',
      nusukSync: 'مزامنة نسك',
      nusukSyncDesc: 'مزامنة تلقائية مع منصة نسك',
      regulatoryCompliance: 'الامتثال التنظيمي',
      saudiRegulations: 'الامتثال للوائح السعودية',
      auditTrail: 'سجل التدقيق',
      auditTrailDesc: 'سجل كامل لجميع التعديلات',
      autoArchiving: 'الأرشفة التلقائية',
      autoArchivingDesc: 'أرشفة تلقائية للتأشيرات المنتهية',
      active: 'نشط',
      warning: 'تحذير',
      inactive: 'غير نشط',
      records: 'سجلات',
      archived: 'مؤرشف',
      // Notification Schedule
      scheduledNotifications: 'الإشعارات المجدولة',
      notificationsPlural: 'إشعارات',
      notificationsSingular: 'إشعار',
      waiting: 'في الانتظار',
      schedule: 'جدولة',
      email: 'بريد إلكتروني',
      sms: 'رسالة نصية',
      whatsapp: 'واتساب',
      recipientsPlural: 'مستلمين',
      recipientsSingular: 'مستلم',
      scheduledFor: 'مجدول لـ',
      french: 'الفرنسية',
      arabic: 'العربية',
      noScheduledNotifications: 'لا توجد إشعارات مجدولة',
      // Visa Details Modal
      visaDetails: 'تفاصيل التأشيرة',
      passportNumber: 'رقم جواز السفر',
      applicationDetails: 'تفاصيل الطلب',
      visaStatus: 'حالة التأشيرة',
      statusTimeline: 'الجدول الزمني للحالة',
      applicationSubmitted: 'تم تقديم الطلب',
      currentStage: 'المرحلة الحالية',
      visaIssued: 'تأشيرة صادرة',
      downloadVisa: 'تحميل التأشيرة',
      print: 'طباعة'
    },
    documents: {
      title: 'إدارة المستندات',
      subtitle: 'إنشاء وإدارة جميع مستنداتك',
      contracts: 'العقود',
      contractsDesc: 'عقود العمرة والحج',
      officialLists: 'القوائم الرسمية',
      officialListsDesc: 'قوائم الحجاج',
      convocations: 'الاستدعاءات',
      convocationsDesc: 'مستندات الاستدعاء',
      attestations: 'الشهادات',
      attestationsDesc: 'شهادات العمرة/الحج',
      invoices: 'الفواتير',
      invoicesDesc: 'الفواتير والإيصالات',
      overview: 'نظرة عامة',
      storage: 'التخزين',
      signatures: 'التوقيعات',
      templates: 'النماذج',
      newDocumentSigned: 'مستند جديد موقع',
      newDocumentSignedDesc: 'تم توقيع عقد أحمد بن علي بنجاح',
      signaturePending: 'توقيع قيد الانتظار',
      signaturePendingDesc: '3 مستندات تحتاج إلى توقيعك',
      generateSuccess: 'تم إنشاء المستندات بنجاح لـ',
      secureStorage: 'التخزين الآمن',
      searchDocuments: 'البحث عن المستندات...',
      noDocumentsFound: 'لم يتم العثور على مستندات',
      preview: 'معاينة',
      download: 'تحميل',
      share: 'مشاركة',
      version: 'الإصدار',
      signatureWorkflows: 'سير عمل التوقيع',
      newWorkflow: 'سير عمل جديد',
      progress: 'التقدم',
      createdOn: 'تم الإنشاء في',
      deadline: 'الموعد النهائي',
      signed: 'وقع',
      generateDocuments: 'إنشاء المستندات',
      documentTemplate: 'نموذج المستند',
      chooseFormat: 'اختر تنسيق المستند',
      pilgrimGroup: 'مجموعة الحجاج',
      selectGroup: 'حدد المجموعة المعنية',
      documentLanguage: 'لغة المستند',
      includeSignature: 'تضمين التوقيع الإلكتروني',
      addWorkflow: 'إضافة سير عمل التوقيع',
      bulkGeneration: 'الإنشاء الجماعي',
      generateForAll: 'إنشاء لجميع الحجاج في المجموعة',
      importantInfo: 'معلومات مهمة',
      autoSaveInfo: 'سيتم حفظ المستندات المُنشأة تلقائيًا في نظام التخزين الآمن وإرسالها عبر البريد الإلكتروني إلى المستلمين المعنيين.',
      cancel: 'إلغاء',
      generate: 'إنشاء',
      templateManagement: 'إدارة النماذج',
      newTemplate: 'نموذج جديد',
      bilingual: 'ثنائي اللغة',
      usages: 'استخدام',
      modify: 'تعديل',
      templateCustomization: 'تخصيص النموذج',
      header: 'الرأسية',
      footer: 'التذييل',
      agencyLogo: 'شعار الوكالة',
      legalInfo: 'المعلومات القانونية',
      newSignatureWorkflow: 'سير عمل توقيع جديد',
      configureSignature: 'تكوين عملية التوقيع',
      close: 'إغلاق',
      workflowName: 'اسم سير العمل',
      workflowNamePlaceholder: 'مثال: عقود المجموعة أ - يناير 2026',
      documentToSign: 'المستند المراد توقيعه',
      deadlineDate: 'الموعد النهائي',
      signers: 'الموقعون',
      add: 'إضافة',
      fullName: 'الاسم الكامل',
      email: 'البريد الإلكتروني',
      remove: 'حذف',
      signatureProcess: 'عملية التوقيع',
      signatureProcessInfo: 'سيتلقى الموقعون بريدًا إلكترونيًا يحتوي على رابط آمن لتوقيع المستند إلكترونيًا. سيتم إعلامك بكل توقيع.',
      createWorkflow: 'إنشاء سير العمل',
      shareDocument: 'مشاركة المستند',
      recipientEmail: 'البريد الإلكتروني للمستلم',
      sendMeCopy: 'إرسال نسخة لي',
      receiveCopy: 'استلام نسخة من المستند عبر البريد الإلكتروني',
      includeMessage: 'تضمين رسالة',
      addCustomMessage: 'إضافة رسالة مخصصة',
      message: 'الرسالة',
      enterMessage: 'أدخل رسالتك هنا...',
      shareInfo: 'سيتلقى المستلم رابطًا آمنًا لتنزيل المستند. سينتهي صلاحية الرابط بعد 7 أيام.',
      editTemplate: 'تعديل النموذج',
      previewTemplate: 'معاينة النموذج',
      templateSettings: 'إعدادات النموذج',
      templateName: 'اسم النموذج',
      language: 'اللغة',
      usage: 'الاستخدام',
      documentsGenerated: 'مستند تم إنشاؤه',
      previewLabel: 'معاينة',
      templatePreview: 'هذه معاينة لنموذج المستند. سيتم إنشاء المحتوى الفعلي ديناميكيًا بناءً على بيانات الحاج والمجموعة.',
      availableVariables: 'المتغيرات المتاحة:',
      autoGenerated: 'مستند تم إنشاؤه تلقائيًا - المعلومات القانونية وتفاصيل الاتصال بالوكالة',
      save: 'حفظ',
      documentPreview: 'معاينة المستند',
      pilgrim: 'الحاج',
      documentDetails: 'تفاصيل المستند',
      fileSize: 'حجم الملف',
      uploadDate: 'تاريخ التحميل',
      status: 'الحالة',
      pdfPreview: 'معاينة مستند PDF',
      contentWillDisplay: 'سيتم عرض محتوى المستند هنا',
      total: 'المجموع',
      standardTemplate: 'نموذج قياسي',
      detailedTemplate: 'نموذج مفصل',
      simplifiedTemplate: 'نموذج مبسط',
      french: 'Français',
      arabic: 'العربية',
      both: 'ثنائي اللغة',
      administrator: 'مدير',
      agent: 'وكيل',
      completed: 'مكتمل',
      sent: 'مُرسل'
    },
    communication: {
      title: 'مركز الاتصالات',
      subtitle: 'التواصل مع الحجاج',
      compose: 'إنشاء',
      timeline: 'السجل',
      templates: 'القوالب',
      scheduled: 'المجدولة',
      integrations: 'التكاملات',
      analytics: 'التحليلات',
      messageDelivered: 'تم تسليم الرسالة',
      messageDeliveredDesc: 'تم تسليم رسالتك إلى 45 حاجاً بنجاح',
      deliveryFailed: 'فشل التسليم',
      deliveryFailedDesc: 'لم يتم تسليم 3 رسائل. تحقق من أرقام الهواتف'
    },
    analytics: {
      title: 'لوحة التحليلات',
      subtitle: 'تحليل أداء وكالتك',
      exportPDF: 'تصدير PDF',
      exportCSV: 'تصدير CSV',
      exporting: 'تصدير...',
      revenue: 'الإيرادات',
      pilgrims: 'الحجاج',
      currentPeriod: 'الفترة الحالية',
      previousPeriod: 'الفترة السابقة',
      threeMonths: '3 أشهر',
      sixMonths: '6 أشهر',
      oneYear: 'سنة',
      pilgrimConversionRate: 'معدل تحويل الحجاج',
      avgRevenuePerPilgrim: 'متوسط الإيرادات لكل حاج',
      documentCompletionRate: 'معدل إكمال المستندات',
      seasonalPerformance: 'الأداء الموسمي',
      revenueTrend: 'اتجاه الإيرادات',
      conversionFunnel: 'قمع التحويل',
      performanceComparison: 'مقارنة الأداء'
    },
    financial: {
      title: 'الإدارة المالية',
      subtitle: 'إدارة المعاملات والربحية',
      totalRevenue: 'إجمالي الإيرادات',
      outstandingBalance: 'الرصيد المستحق',
      refundsProcessing: 'المبالغ المستردة قيد المعالجة',
      groupProfitability: 'الربحية حسب المجموعة',
      vsLastMonth: 'مقارنة بالشهر الماضي',
      vsLastQuarter: 'مقارنة بالربع الماضي',
      transactions: 'المعاملات',
      revenue: 'الإيرادات',
      costs: 'التكاليف',
      profit: 'الربح',
      margin: 'الهامش',
      viewDetails: 'عرض التفاصيل',
      export: 'تصدير',
      currency: 'درهم',
      profitabilityAnalysis: 'تحليل الربحية',
      costEntry: 'إدخال التكاليف',
      addCostEntry: 'إضافة تكلفة',
      bulkCostImport: 'استيراد جماعي',
      roiAnalysisTitle: 'تحليل العائد على الاستثمار والربحية',
      roiAnalysisDescription: 'عرض مقاييس الربحية والاتجاهات الموسمية وتحليل نقطة التعادل',
      exportPDF: 'تصدير PDF',
      exportCSV: 'تصدير CSV',
      hide: 'إخفاء',
      showAnalysis: 'عرض التحليل',
      quickActions: 'إجراءات سريعة',
      processPayment: 'معالجة الدفع',
      generateInvoice: 'إنشاء فاتورة',
      processRefund: 'معالجة الاسترداد',
      exportAccounting: 'تصدير المحاسبة',
      generateContract: 'إنشاء عقد',
      viewReports: 'عرض التقارير',
      filterBy: 'تصفية حسب',
      status: 'الحالة',
      allStatuses: 'جميع الحالات',
      completed: 'مكتمل',
      pending: 'معلق',
      overdue: 'متأخر',
      refunded: 'مسترد',
      transactionType: 'نوع المعاملة',
      allTypes: 'جميع الأنواع',
      group: 'المجموعة',
      allGroups: 'جميع المجموعات',
      searchPilgrim: 'بحث عن حاج',
      pilgrimName: 'اسم الحاج',
      amount: 'المبلغ',
      type: 'النوع',
      method: 'الطريقة',
      dueDate: 'تاريخ الاستحقاق',
      actions: 'الإجراءات',
      noTransactions: 'لم يتم العثور على معاملات',
      generateInvoiceAction: 'إنشاء فاتورة',
      showing: 'عرض',
      of: 'من',
      pilgrims: 'حجاج',
      description: 'الوصف',
      vendor: 'المورد',
      referenceNumber: 'رقم المرجع',
      reference: 'المرجع',
      importInstructions: 'تعليمات الاستيراد',
      preview: 'معاينة',
      editBudget: 'تعديل الميزانية',
      setBudget: 'تحديد الميزانية',
      totalBudget: 'إجمالي الميزانية',
      categoryBreakdown: 'التوزيع حسب الفئة',
      accommodation: 'الإقامة',
      transport: 'النقل',
      meals: 'الوجبات',
      guides: 'المرشدين',
      visasCost: 'التأشيرات',
      other: 'أخرى',
      budgetAnalysis: 'تحليل الميزانية',
      budgetVsActual: 'الميزانية مقابل الفعلي',
      actualCosts: 'التكاليف الفعلية',
      variance: 'الفرق',
      utilization: 'الاستخدام',
      cash: 'نقدي',
      bankTransfer: 'تحويل بنكي',
      check: 'شيك',
      bankCard: 'بطاقة بنكية',
      downPayment: 'دفعة مقدمة',
      balancePayment: 'الرصيد',
      adjustment: 'تعديل',
      pilgrimInfo: 'معلومات الحاج',
      name: 'الاسم',
      number: 'الرقم',
      paymentMethod: 'طريقة الدفع',
      notes: 'ملاحظات',
      cancel: 'إلغاء',
      processing: 'جاري المعالجة...',
      quantity: 'الكمية',
      unitCost: 'تكلفة الوحدة',
      entryDate: 'تاريخ الإدخال',
      saving: 'جاري الحفظ...',
      saveCost: 'حفظ التكلفة',
      selectGroup: 'اختر مجموعة',
      uploadCSV: 'تحميل CSV',
      downloadTemplate: 'تنزيل النموذج',
      category: 'الفئة',
      excellent: 'ممتاز',
      good: 'جيد',
      average: 'متوسط',
      poor: 'ضعيف',
      roiRankings: 'تصنيف العائد على الاستثمار للمجموعات',
      revenues: 'الإيرادات',
      fullRanking: 'التصنيف الكامل',
      rank: 'الترتيب',
      performance: 'الأداء',
      revenueVsCosts: 'مقارنة الإيرادات مقابل التكاليف (أفضل 10)',
      noDataAvailable: 'لا توجد بيانات متاحة',
      loading: 'جاري التحميل...',
      noBudgetDefined: 'لم يتم تحديد ميزانية لهذه المجموعة',
      successBudgetSaved: 'تم حفظ الميزانية بنجاح',
      errorSaving: 'خطأ في الحفظ',
      successCostSaved: 'تم تسجيل التكلفة بنجاح',
      errorCostSaving: 'خطأ في تسجيل التكلفة',
      selectCSVFile: 'يرجى اختيار ملف CSV',
      emptyCSVFile: 'ملف CSV فارغ',
      selectGroupFirst: 'يرجى اختيار مجموعة',
      noDataToImport: 'لا توجد بيانات للاستيراد',
      costsImportedSuccess: 'تكلفة تم استيرادها بنجاح',
      errorImporting: 'خطأ في الاستيراد',
      entries: 'إدخالات',
      invoiceGenerated: 'تم إنشاء فاتورة لـ',
      noPendingTransactions: 'لم يتم العثور على معاملات معلقة',
      enterRefundAmount: 'أدخل مبلغ الاسترداد (درهم):',
      refundProcessing: 'جاري معالجة استرداد',
      madProcessing: 'درهم',
      noCompletedTransactions: 'لم يتم العثور على معاملات مكتملة للاسترداد',
      accountingExported: 'تم تصدير البيانات المحاسبية بنجاح',
      emptyTemplate: 'نموذج فارغ',
      enterGroupId: 'أدخل معرف المجموعة لتطبيق هذا النموذج:',
      templateAppliedSuccess: 'تم تطبيق النموذج بنجاح',
      errorApplying: 'خطأ في التطبيق'
    },
    pilgrimProfile: {
      // Header & Actions
      caseNumber: 'رقم الملف',
      requestDocument: 'Demander document',
      processPayment: 'Traiter paiement',
      assignToGroup: 'Assigner au groupe',
      sendMessage: 'Envoyer message',
      
      // Status
      active: 'Actif',
      pending: 'En attente',
      completed: 'Terminé',
      cancelled: 'Annulé',
      
      // Tabs
      personalInfo: 'Informations personnelles',
      documents: 'Documents',
      travelInfo: 'Informations de voyage',
      financial: 'Financier',
      caseHistory: 'Historique du dossier',
      
      // Personal Info Tab
      edit: 'Modifier',
      save: 'Enregistrer',
      cancel: 'Annuler',
      identityDetails: 'Détails d\'identité',
      firstName: 'Prénom',
      lastName: 'Nom',
      arabicFirstName: 'Prénom en arabe',
      arabicLastName: 'Nom en arabe',
      dateOfBirth: 'Date de naissance',
      placeOfBirth: 'Lieu de naissance',
      gender: 'Genre',
      male: 'Homme',
      female: 'Femme',
      nationality: 'Nationalité',
      moroccan: 'Marocain',
      nationalId: 'Carte d\'identité nationale',
      
      // Passport Info
      passportInfo: 'Informations du passeport',
      passportNumber: 'Numéro de passeport',
      passportIssueDate: 'Date d\'émission',
      passportExpiryDate: 'Date d\'expiration',
      passportIssuePlace: 'Lieu d\'émission',
      
      // Contact Info
      contactInfo: 'Coordonnées',
      email: 'Email',
      phone: 'Téléphone',
      whatsapp: 'WhatsApp',
      address: 'Adresse',
      city: 'Ville',
      postalCode: 'Code postal',
      
      // Emergency Contact
      emergencyContact: 'Contact d\'urgence',
      emergencyContactName: 'Nom du contact',
      emergencyContactRelation: 'Relation',
      emergencyContactPhone: 'Téléphone',
      spouse: 'Conjoint',
      parent: 'Parent',
      sibling: 'Frère/Sœur',
      child: 'Enfant',
      other: 'Autre',
      
      // Documents Tab
      passportCopy: 'Copie du passeport',
      photo: 'Photo d\'identité',
      medicalCertificate: 'Certificat médical',
      visaDocument: 'Document de visa',
      notUploaded: 'Non téléchargé',
      approved: 'Approuvé',
      rejected: 'Rejeté',
      approvedOn: 'Approuvé le',
      rejectedOn: 'Rejeté le',
      uploadedOn: 'Téléchargé le',
      view: 'Voir',
      approve: 'Approuver',
      reject: 'Rejeter',
      upload: 'Télécharger',
      
      // Travel Info Tab
      groupAssignment: 'Affectation au groupe',
      groupName: 'Nom du groupe',
      groupType: 'Type de groupe',
      departureDate: 'Date de départ',
      returnDate: 'Date de retour',
      accommodation: 'Hébergement',
      makkahHotel: 'Hôtel à La Mecque',
      madinahHotel: 'Hôtel à Médine',
      roomType: 'Type de chambre',
      flightInfo: 'Informations de vol',
      outboundFlight: 'Vol aller',
      returnFlight: 'Vol retour',
      flightNumber: 'Numéro de vol',
      airline: 'Compagnie aérienne',
      departure: 'Départ',
      arrival: 'Arrivée',
      nusukSync: 'Synchronisation Nusuk',
      status: 'Statut',
      synced: 'Synchronisé',
      lastSync: 'Dernière synchronisation',
      itinerary: 'Itinéraire',
      
      // Financial Tab
      totalAmount: 'Montant total',
      paidAmount: 'Montant payé',
      remainingBalance: 'Solde restant',
      paymentHistory: 'Historique des paiements',
      date: 'Date',
      amount: 'Montant',
      method: 'Méthode',
      reference: 'Référence',
      paid: 'Payé',
      refunded: 'Remboursé',
      cash: 'Espèces',
      card: 'Carte',
      transfer: 'Virement',
      cheque: 'Chèque',
      invoices: 'Factures',
      invoiceNumber: 'Numéro de facture',
      downloadInvoice: 'Télécharger facture',
      generateInvoice: 'Générer facture',
      
      // Case History Tab
      timeline: 'Chronologie',
      noHistory: 'Aucun historique disponible',
      fileCreated: 'Dossier créé',
      fileCreatedDesc: 'Nouveau dossier de pèlerin créé dans le système',
      documentsUploaded: 'Documents téléchargés',
      documentsUploadedDesc: 'Passeport et photo d\'identité téléchargés',
      documentsApproved: 'Documents approuvés',
      documentsApprovedDesc: 'Tous les documents ont été vérifiés et approuvés',
      paymentReceived: 'Paiement reçu',
      assignedToGroup: 'Assigné au groupe',
      documentsLabel: 'Documents',
      passportPhoto: 'Passeport, Photo',
      amountLabel: 'Montant',
      methodLabel: 'Méthode',
      groupLabel: 'Groupe',
      
      // ProfileHeader prompts and alerts
      enterGroupName: 'Entrez le nom du groupe pour assigner ce pèlerin:',
      assignmentSuccess: '{firstName} {lastName} a été assigné au groupe "{groupName}" avec succès!',
      invalidGroupName: 'Nom de groupe invalide!',
      
      // Mock data translations
      firstPaymentReceived: 'Premier acompte de 10,000 MAD reçu',
      assignedToGroupDesc: 'Affecté au Groupe Omra Mars 2026',
      marchOmraGroup: 'Groupe Omra Mars 2026',
      standardOmra: 'Omra Standard',
      darAlTawhidHotel: 'Hôtel Dar Al Tawhid',
      alAqeeqHotel: 'Hôtel Al Aqeeq',
      doubleRoom: 'Chambre double',
      itineraryDescription: '7 jours à La Mecque, 7 jours à Médine avec visites des lieux saints et accompagnement spirituel complet',
      
      // Notifications
      passportApprovedNotification: 'Le passeport de Fatima Hassan a été approuvé',
      paymentReceivedNotification: 'Paiement de 5000 MAD reçu pour le dossier #OM2026-1234',
      
      // Invoice and Payment handlers
      invoiceGenerationMessage: 'Génération de la facture pour {firstName} {lastName}...\nMontant total: {totalAmount} MAD\nMontant payé: {paidAmount} MAD\nSolde restant: {remainingBalance} MAD',
      enterPaymentAmount: 'Entrez le montant du paiement (Solde restant: {remainingBalance} MAD):',
      amountExceedsBalance: 'Le montant saisi dépasse le solde restant!',
      selectPaymentMethod: 'Méthode de paiement (cash/card/transfer/cheque):',
      paymentSuccessMessage: 'Paiement de {amount} MAD enregistré avec succès!\nMéthode: {method}\nNouveau solde: {newBalance} MAD',
      invalidAmount: 'Montant invalide!'
    },
    login: {
      title: 'تسجيل الدخول',
      subtitle: 'سجّل دخولك إلى لوحة إدارة الحج والعمرة',
      email: 'البريد الإلكتروني',
      emailPlaceholder: 'بريدك@الإلكتروني.com',
      emailRequired: 'البريد الإلكتروني مطلوب',
      password: 'كلمة المرور',
      passwordPlaceholder: 'كلمة المرور',
      passwordRequired: 'كلمة المرور مطلوبة',
      rememberMe: 'تذكرني',
      forgotPassword: 'نسيت كلمة المرور؟',
      signIn: 'تسجيل الدخول',
      invalidCredentials: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      noAccount: 'ليس لديك حساب؟',
      register: 'إنشاء حساب',
      networkError: 'خطأ في الاتصال. تحقق من اتصالك بالإنترنت.',
      loginSuccess: 'تم تسجيل الدخول بنجاح'
    },
    superAdmin: {
      totalAgencies: 'إجمالي الوكالات',
      activeSubscriptions: 'الاشتراكات النشطة',
      monthlyRevenue: 'الإيرادات الشهرية',
      conversionRate: 'معدل التحويل',
      vsLastMonth: 'مقارنة بالشهر الماضي',
      agencyLabel: 'الوكالة:',
      // Subscription Plans
      allPlans: 'جميع الخطط',
      basicPlan: 'الخطة الأساسية',
      standardPlan: 'الخطة القياسية',
      premiumPlan: 'الخطة المميزة',
      freeTrial: 'تجربة مجانية',
      // Status
      allStatuses: 'جميع الحالات',
      activeStatus: 'نشط',
      suspendedStatus: 'معلق',
      trialStatus: 'تجريبي'
    }
  }
};
export function LanguageProvider({ children }) {
  const [currentLanguage, setCurrentLanguage] = useState('fr');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'fr';
    setCurrentLanguage(savedLanguage);
    document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = savedLanguage;
  }, []);

  const switchLanguage = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  const t = (key) => {
    const keys = key?.split('.');
    let value = translations?.[currentLanguage];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    
    return value || key;
  };

  let value = {
    currentLanguage,
    switchLanguage,
    t,
    translations: translations?.[currentLanguage]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export default LanguageProvider;